import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AllocationDialog } from '../../components/AllocationDialog';
import { ReverseAllocationDialog } from '../../components/ReverseAllocationDialog';
import { SplitTransactionDialog } from '../../components/SplitTransactionDialog';
import {
  DollarSign,
  Plus,
  } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  API_URL,
  formatCurrency,
} from './transactions/utils/formatters';
import { TransactionFormDialog } from './transactions/dialogs/TransactionFormDialog';
import { LinkStagingInvestorDialog } from './transactions/dialogs/LinkStagingInvestorDialog';
import { AllocateStatementRowDialog } from './transactions/dialogs/AllocateStatementRowDialog';
import { DeleteWithReasonDialog } from './transactions/dialogs/DeleteWithReasonDialog';
import { BulkEditDialog } from './transactions/dialogs/BulkEditDialog';
import { MatchConfirmDialog } from './transactions/dialogs/MatchConfirmDialog';
import { ManageSplitsDialog } from './transactions/dialogs/ManageSplitsDialog';
import { BankSplitDialog } from './transactions/dialogs/BankSplitDialog';
import { InspectRecordedAllocationsDialog } from './transactions/dialogs/InspectRecordedAllocationsDialog';
import { InspectBankAllocationsDialog } from './transactions/dialogs/InspectBankAllocationsDialog';
import { ReassignBankInvestorDialog } from './transactions/dialogs/ReassignBankInvestorDialog';
import { ReimportExcelDialog } from './transactions/dialogs/ReimportExcelDialog';
import { TransactionsTabPanel } from './transactions/tabs/TransactionsTabPanel';
import { ImportCsvTabPanel } from './transactions/tabs/ImportCsvTabPanel';
import { BankStatementTabPanel } from './transactions/tabs/BankStatementTabPanel';
import { ReconciliationTabPanel } from './transactions/tabs/ReconciliationTabPanel';
import { useBankStatement } from './transactions/hooks/useBankStatement';
import { useTransactionsData } from './transactions/hooks/useTransactionsData';
import { makeReconRowFilter, reconFiltersActive } from './transactions/utils/reconFilters';
import { useReconciliation } from './transactions/hooks/useReconciliation';
import { useCsvImport } from './transactions/hooks/useCsvImport';
import { useTransactionCrud } from './transactions/hooks/useTransactionCrud';

export default function AdminTransactionsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // ── Main data hook (transactions list, investors, filters, sort) ──
  const {
    loading,
    transactions,
    investors,
    reconInvestors,
    reconClearIds,
    pagination, setPagination,
    summary,
    investorFilter, setInvestorFilter,
    typeFilter, setTypeFilter,
    startDate, setStartDate,
    endDate, setEndDate,
    sortTxn, setSortTxn,
    fetchTransactions,
    fetchReconTicks,
    clearFilters,
  } = useTransactionsData({ token, headers });

  // ── Sub-hooks — each owns its tab's state + handlers ──
  const bank = useBankStatement({ token, headers });
  const recon = useReconciliation({ token, headers, fetchTransactions, onReconRefreshed: fetchReconTicks });
  const csv = useCsvImport({ token, headers, fetchTransactions });
  const crud = useTransactionCrud({ headers, fetchTransactions });

  // ── Tab + bulk-edit state (kept here — bulk edit is cross-tab) ──
  const [activeTab, setActiveTab] = useState('transactions');
  const [sortStmt, setSortStmt] = useState({ key: 'date', dir: 'desc' });
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditInvestorId, setBulkEditInvestorId] = useState('');
  const [bulkEditTxnType, setBulkEditTxnType] = useState('');
  const [bulkEditTarget, setBulkEditTarget] = useState('committed');
  const [bulkEditing, setBulkEditing] = useState(false);

  // Shared sort-toggle helper (pure)
  const toggleSort = (setter) => (key) => {
    setter(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  // Cross-tab bulk edit — writes to committed OR staging depending on target.
  const handleBulkEdit = async () => {
    const ids = bulkEditTarget === 'staging' ? [...csv.selectedImport] : [...crud.selectedMain];
    if (ids.length === 0) return;
    const payload = { ids };
    if (bulkEditInvestorId) payload.investor_id = bulkEditInvestorId;
    if (bulkEditTxnType) payload.transaction_type = bulkEditTxnType;
    if (!payload.investor_id && !payload.transaction_type) {
      toast.error('Select at least one field to update');
      return;
    }
    setBulkEditing(true);
    try {
      const url = bulkEditTarget === 'staging'
        ? `${API_URL}/api/admin/investor-transactions/staging/bulk-update`
        : `${API_URL}/api/admin/investor-transactions/bulk-update`;
      const r = await axios.put(url, payload, { headers });
      toast.success(r.data.message || `${ids.length} records updated`);
      setBulkEditOpen(false);
      setBulkEditInvestorId('');
      setBulkEditTxnType('');
      if (bulkEditTarget === 'staging') {
        csv.setSelectedImport(new Set());
        csv.fetchBatches();
      } else {
        crud.setSelectedMain(new Set());
        fetchTransactions();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkEditing(false); }
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="space-y-6" data-testid="transactions-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // ═══ ctx bundle for tab panels + retained dialogs ═══
  const ctx = {
    headers,
    transactions,
    investors,
    summary,
    investorFilter, setInvestorFilter,
    typeFilter, setTypeFilter,
    startDate, setStartDate,
    endDate, setEndDate,
    fetchTransactions,
    clearFilters,
    // Main-tab bulk selection + CRUD
    selectedMain: crud.selectedMain,
    setSelectedMain: crud.setSelectedMain,
    handleBulkDelete: crud.handleBulkDelete,
    bulkDeleting: crud.bulkDeleting,
    handleEdit: crud.handleEdit,
    handleDelete: crud.handleDelete,
    // Sort helpers
    sortTxn, setSortTxn, toggleSort,
    toggleSelectAll: (setter, ids) => {
      setter(prev => prev.size === ids.length ? new Set() : new Set(ids));
    },
    toggleSelect: (setter) => (id) => {
      setter(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    },
    pagination, setPagination,
    // Cross-tab bulk-edit
    setBulkEditTarget, setBulkEditOpen,
    // Import tab
    handleDownloadTemplate: csv.handleDownloadTemplate,
    importFile: csv.importFile, setImportFile: csv.setImportFile,
    setImportResult: csv.setImportResult,
    importForce: csv.importForce, setImportForce: csv.setImportForce,
    handleImport: csv.handleImport,
    importing: csv.importing,
    importResult: csv.importResult,
    // Import & Approve — pending staging batches (previously fetched by
    // this hook but never rendered anywhere; the only screen this data
    // could be seen/acted on from didn't exist).
    batches: csv.batches, batchesLoading: csv.batchesLoading,
    selectedBatch: csv.selectedBatch, setSelectedBatch: csv.setSelectedBatch,
    batchDetail: csv.batchDetail, batchDetailLoading: csv.batchDetailLoading,
    showCompletedBatches: csv.showCompletedBatches, setShowCompletedBatches: csv.setShowCompletedBatches,
    fetchBatches: csv.fetchBatches, fetchBatchDetail: csv.fetchBatchDetail,
    handleApproveBatch: csv.handleApproveBatch, handleRejectBatch: csv.handleRejectBatch, handleDeleteBatch: csv.handleDeleteBatch,
    handleApproveItem: csv.handleApproveItem, handleRejectItem: csv.handleRejectItem,
    linkDialog: csv.linkDialog, setLinkDialog: csv.setLinkDialog,
    linkInvestorId: csv.linkInvestorId, setLinkInvestorId: csv.setLinkInvestorId,
    handleLinkInvestor: csv.handleLinkInvestor,
    editingStaging: csv.editingStaging, setEditingStaging: csv.setEditingStaging,
    editStagingType: csv.editStagingType, setEditStagingType: csv.setEditStagingType,
    editStagingAmount: csv.editStagingAmount, setEditStagingAmount: csv.setEditStagingAmount,
    editStagingDate: csv.editStagingDate, setEditStagingDate: csv.setEditStagingDate,
    editStagingNotes: csv.editStagingNotes, setEditStagingNotes: csv.setEditStagingNotes,
    handleEditStaging: csv.handleEditStaging, handleSaveEditStaging: csv.handleSaveEditStaging,
    selectedImport: csv.selectedImport, setSelectedImport: csv.setSelectedImport,
    toggleSelectImport: csv.toggleSelectImport, toggleSelectAllImport: csv.toggleSelectAllImport,
    setBulkEditOpen, setBulkEditTarget,
    // Bank statement tab
    statementInvestor: bank.statementInvestor, setStatementInvestor: bank.setStatementInvestor,
    fetchStatement: bank.fetchStatement,
    statementLoading: bank.statementLoading,
    statement: bank.statement,
    selectedStmt: bank.selectedStmt, setSelectedStmt: bank.setSelectedStmt,
    sortStmt, setSortStmt,
    setAllocateDialog: bank.setAllocateDialog,
    // Reconciliation tab
    reconData: recon.reconData, setReconData: recon.setReconData,
    reconLoading: recon.reconLoading,
    reconInvestorFilter: recon.reconInvestorFilter, setReconInvestorFilter: recon.setReconInvestorFilter,
    reconIncludeUntagged: recon.reconIncludeUntagged, setReconIncludeUntagged: recon.setReconIncludeUntagged,
    reconFromDate: recon.reconFromDate, setReconFromDate: recon.setReconFromDate,
    reconToDate: recon.reconToDate, setReconToDate: recon.setReconToDate,
    reconSearch: recon.reconSearch, setReconSearch: recon.setReconSearch,
    reconInvestors,
    reconClearIds,
    selectedRecordedTxn: recon.selectedRecordedTxn, setSelectedRecordedTxn: recon.setSelectedRecordedTxn,
    selectedBankTxn: recon.selectedBankTxn, setSelectedBankTxn: recon.setSelectedBankTxn,
    setMatchConfirmOpen: recon.setMatchConfirmOpen,
    selectedReconRecorded: recon.selectedReconRecorded, setSelectedReconRecorded: recon.setSelectedReconRecorded,
    selectedReconBank: recon.selectedReconBank, setSelectedReconBank: recon.setSelectedReconBank,
    reconBulkDeleting: recon.reconBulkDeleting,
    selectedMatched: recon.selectedMatched, setSelectedMatched: recon.setSelectedMatched,
    toggleMatched: recon.toggleMatched,
    expandedMatched: recon.expandedMatched, setExpandedMatched: recon.setExpandedMatched,
    showMatched: recon.showMatched, setShowMatched: recon.setShowMatched,
    showSuggestions: recon.showSuggestions, setShowSuggestions: recon.setShowSuggestions,
    showUnmatchedUploaded: recon.showUnmatchedUploaded, setShowUnmatchedUploaded: recon.setShowUnmatchedUploaded,
    showUnmatchedBank: recon.showUnmatchedBank, setShowUnmatchedBank: recon.setShowUnmatchedBank,
    suggestionBusyId: recon.suggestionBusyId,
    multiAllocLoading: recon.multiAllocLoading,
    sortRecon: recon.sortRecon, setSortRecon: recon.setSortRecon,
    sortReconRecorded: recon.sortReconRecorded, setSortReconRecorded: recon.setSortReconRecorded,
    sortReconBank: recon.sortReconBank, setSortReconBank: recon.setSortReconBank,
    fetchReconciliation: recon.fetchReconciliation,
    runDedupe: recon.runDedupe,
    runFixAllocatedCashflows: recon.runFixAllocatedCashflows,
    runPromoteConfirmedStaging: recon.runPromoteConfirmedStaging,
    handleAllocateMultiBank: recon.handleAllocateMultiBank,
    handleReconBulkUnmatch: recon.handleReconBulkUnmatch,
    handleReconBulkVerifyRecorded: recon.handleReconBulkVerifyRecorded,
    handleReconBulkDeleteRecorded: recon.handleReconBulkDeleteRecorded,
    handleReconBulkVerifyBank: recon.handleReconBulkVerifyBank,
    handleReconBulkDeleteBank: recon.handleReconBulkDeleteBank,
    handleReconBulkForceCleanRecorded: recon.handleReconBulkForceCleanRecorded,
    handleReconBulkForceCleanBank: recon.handleReconBulkForceCleanBank,
    handleResplitMatched: recon.handleResplitMatched,
    handleUndoVerifyNoMatch: recon.handleUndoVerifyNoMatch,
    handleUnmatch: recon.handleUnmatch,
    handleOpenReverseAllocation: recon.handleOpenReverseAllocation,
    handleOpenSplit: recon.handleOpenSplit,
    handleVerifyNoMatch: recon.handleVerifyNoMatch,
    handleOpenAllocation: recon.handleOpenAllocation,
    handleOpenBankSplit: recon.handleOpenBankSplit,
    handleUnsplitBank: recon.handleUnsplitBank,
    openInspect: recon.openInspect,
    openInspectRecorded: recon.openInspectRecorded,
    openManageSplits: recon.openManageSplits,
    acceptSuggestion: recon.acceptSuggestion,
    rejectSuggestion: recon.rejectSuggestion,
    selectedSuggestions: recon.selectedSuggestions,
    toggleSuggestionSelected: recon.toggleSuggestionSelected,
    toggleAllSuggestionsSelected: recon.toggleAllSuggestionsSelected,
    bulkSuggestionLoading: recon.bulkSuggestionLoading,
    bulkAcceptSuggestions: recon.bulkAcceptSuggestions,
    bulkRejectSuggestions: recon.bulkRejectSuggestions,
    toggleReconRecorded: recon.toggleReconRecorded,
    toggleReconBank: recon.toggleReconBank,
    toggleAllReconRecorded: recon.toggleAllReconRecorded,
    toggleAllReconBank: recon.toggleAllReconBank,
    setReimportOpen: recon.setReimportOpen,
    setReimportFile: recon.setReimportFile,
    setReimportResult: recon.setReimportResult,
    setReassignOpen: recon.setReassignOpen,
  };

  // The Reconciliation search / date filter also limits what the allocation
  // windows offer (user 2026-10-14).
  const _reconFilterArgs = { search: recon.reconSearch, fromDate: recon.reconFromDate, toDate: recon.reconToDate };
  const reconRowFilter = makeReconRowFilter(_reconFilterArgs);
  const reconFilterNote = reconFiltersActive(_reconFilterArgs)
    ? `Limited to your Reconciliation filter${recon.reconSearch ? ` "${recon.reconSearch}"` : ''}`
      + `${recon.reconFromDate ? ` from ${recon.reconFromDate}` : ''}${recon.reconToDate ? ` to ${recon.reconToDate}` : ''}`
    : null;

  return (
    <div className="space-y-6" data-testid="admin-transactions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-6 w-6" />Investor Transactions
          </h1>
          <p className="text-gray-500 mt-1">Track investments, withdrawals, and distributions</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              crud.setEditingTransaction(null);
              crud.setFormData({
                investor_id: '', transaction_type: '', amount: '',
                transaction_date: new Date().toISOString().split('T')[0],
                reference: '', notes: '',
              });
              crud.setShowCreateDialog(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="add-transaction-btn"
          >
            <Plus className="h-4 w-4 mr-2" />Add Transaction
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v)} data-testid="transactions-tabs">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">Upload CSV</TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-statement">Bank Statement</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">Reconciliation</TabsTrigger>
        </TabsList>

        <TransactionsTabPanel ctx={ctx} />
        <ImportCsvTabPanel ctx={ctx} />
        <BankStatementTabPanel ctx={ctx} />
        <ReconciliationTabPanel ctx={ctx} />
      </Tabs>

      {/* ─── Recon-side dialogs ─── */}
      <MatchConfirmDialog
        open={recon.matchConfirmOpen}
        onOpenChange={recon.setMatchConfirmOpen}
        selectedRecordedTxn={recon.selectedRecordedTxn}
        selectedBankTxn={recon.selectedBankTxn}
        matching={recon.matching}
        handleManualMatch={recon.handleManualMatch}
      />

      <AllocationDialog
        open={recon.allocationOpen}
        onOpenChange={recon.setAllocationOpen}
        bankTxn={recon.allocationBankTxn}
        unmatchedRecorded={(recon.reconData?.unmatched_recorded || []).filter(reconRowFilter)}
        filterNote={reconFilterNote}
        onConfirm={recon.handleAllocationConfirm}
        loading={recon.allocationLoading}
      />

      <ReverseAllocationDialog
        open={recon.reverseAllocOpen}
        onOpenChange={(o) => { recon.setReverseAllocOpen(o); if (!o) recon.setReverseAllocRecorded(null); }}
        recordedTxn={recon.reverseAllocRecorded}
        unmatchedBank={(recon.reconData?.unmatched_bank || []).filter(reconRowFilter)}
        filterNote={reconFilterNote}
        onConfirm={recon.handleReverseAllocationConfirm}
        onUnallocate={recon.handleUnallocateBank}
        onWriteOff={recon.handleWriteOffRecorded}
        loading={recon.reverseAllocLoading}
      />

      <SplitTransactionDialog
        open={recon.splitTxnOpen}
        onOpenChange={(o) => { recon.setSplitTxnOpen(o); if (!o) recon.setSplitTxnTarget(null); }}
        transaction={recon.splitTxnTarget}
        onConfirm={recon.handleConfirmSplit}
        loading={recon.splitTxnLoading}
      />

      <ManageSplitsDialog
        open={recon.manageSplitsOpen}
        onOpenChange={(o) => { recon.setManageSplitsOpen(o); if (!o) { recon.setSplitParents([]); recon.setSplitDiagnostics({}); } }}
        splitParentsLoading={recon.splitParentsLoading}
        splitParents={recon.splitParents}
        splitDiagnostics={recon.splitDiagnostics}
        splitDiagLoadingId={recon.splitDiagLoadingId}
        runSplitDiagnostics={recon.runSplitDiagnostics}
        reassignSplitChildren={recon.reassignSplitChildren}
        unsplitFromManageDialog={recon.unsplitFromManageDialog}
        reconInvestorFilter={recon.reconInvestorFilter}
        headers={headers}
        onUploadedSplitUndone={recon.refresh}
      />

      <BankSplitDialog
        open={recon.bankSplitOpen}
        onOpenChange={(o) => {
          recon.setBankSplitOpen(o);
          if (!o) {
            recon.setBankSplitTarget(null);
            recon.setBankSplitParts([]);
            recon.setBankSplitNotes('');
          }
        }}
        bankSplitTarget={recon.bankSplitTarget}
        bankSplitParts={recon.bankSplitParts}
        setBankSplitParts={recon.setBankSplitParts}
        bankSplitNotes={recon.bankSplitNotes}
        setBankSplitNotes={recon.setBankSplitNotes}
        bankSplitLoading={recon.bankSplitLoading}
        handleConfirmBankSplit={recon.handleConfirmBankSplit}
      />

      <InspectRecordedAllocationsDialog
        open={recon.inspectRecOpen}
        onOpenChange={(o) => { recon.setInspectRecOpen(o); if (!o) { recon.setInspectRecId(null); recon.setInspectRecData(null); } }}
        inspectRecId={recon.inspectRecId}
        inspectRecData={recon.inspectRecData}
        inspectRecLoading={recon.inspectRecLoading}
        deleteAllocationDoc={recon.deleteAllocationDoc}
        fetchReconciliation={recon.fetchReconciliation}
        reconInvestorFilter={recon.reconInvestorFilter}
        reconFromDate={recon.reconFromDate}
        reconToDate={recon.reconToDate}
        forceCleanRecorded={recon.forceCleanRecorded}
      />

      <InspectBankAllocationsDialog
        open={recon.inspectOpen}
        onOpenChange={(o) => { recon.setInspectOpen(o); if (!o) { recon.setInspectBankId(null); recon.setInspectData(null); } }}
        inspectBankId={recon.inspectBankId}
        inspectData={recon.inspectData}
        inspectLoading={recon.inspectLoading}
        fetchReconciliation={recon.fetchReconciliation}
        reconInvestorFilter={recon.reconInvestorFilter}
        reconFromDate={recon.reconFromDate}
        reconToDate={recon.reconToDate}
        forceCleanBank={recon.forceCleanBank}
      />

      <ReassignBankInvestorDialog
        open={recon.reassignOpen}
        onOpenChange={(o) => { recon.setReassignOpen(o); if (!o) recon.setReassignInvestor(''); }}
        reassignInvestor={recon.reassignInvestor}
        setReassignInvestor={recon.setReassignInvestor}
        reconInvestors={reconInvestors}
        selectedReconBank={recon.selectedReconBank}
        handleReassignBank={recon.handleReassignBank}
        reassignLoading={recon.reassignLoading}
      />

      <ReimportExcelDialog
        open={recon.reimportOpen}
        onOpenChange={(o) => { recon.setReimportOpen(o); if (!o) { recon.setReimportFile(null); recon.setReimportResult(null); } }}
        reimportFile={recon.reimportFile}
        setReimportFile={recon.setReimportFile}
        reimportResult={recon.reimportResult}
        reimportLoading={recon.reimportLoading}
        handleReimportExcel={recon.handleReimportExcel}
      />

      {/* ─── Main-tab / CRUD dialogs ─── */}
      <TransactionFormDialog
        open={crud.showCreateDialog}
        onOpenChange={crud.setShowCreateDialog}
        editingTransaction={crud.editingTransaction}
        formData={crud.formData}
        setFormData={crud.setFormData}
        investors={investors}
        submitting={crud.submitting}
        handleSubmit={crud.handleSubmit}
      />

      <DeleteWithReasonDialog
        open={crud.deleteReasonOpen}
        onOpenChange={crud.setDeleteReasonOpen}
        pendingDeleteAction={crud.pendingDeleteAction}
        deleteReason={crud.deleteReason}
        setDeleteReason={crud.setDeleteReason}
        executeDelete={crud.executeDelete}
      />

      {/* ─── CSV import dialogs ─── */}
      <LinkStagingInvestorDialog
        linkDialog={csv.linkDialog}
        setLinkDialog={csv.setLinkDialog}
        linkInvestorId={csv.linkInvestorId}
        setLinkInvestorId={csv.setLinkInvestorId}
        investors={investors}
        handleLinkInvestor={csv.handleLinkInvestor}
      />

      {/* ─── Bank-statement dialog ─── */}
      <AllocateStatementRowDialog
        allocateDialog={bank.allocateDialog}
        setAllocateDialog={bank.setAllocateDialog}
        loansList={bank.loansList}
        loansSearch={bank.loansSearch}
        setLoansSearch={bank.setLoansSearch}
        handleAllocate={bank.handleAllocate}
      />

      {/* ─── Cross-tab bulk edit ─── */}
      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        bulkEditTarget={bulkEditTarget}
        selectedImport={csv.selectedImport}
        selectedMain={crud.selectedMain}
        bulkEditInvestorId={bulkEditInvestorId}
        setBulkEditInvestorId={setBulkEditInvestorId}
        bulkEditTxnType={bulkEditTxnType}
        setBulkEditTxnType={setBulkEditTxnType}
        bulkEditing={bulkEditing}
        handleBulkEdit={handleBulkEdit}
      />
    </div>
  );
}
